import { add } from 'date-fns';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import { SUB_PATHS } from '@/constants/urlConstants';
import { DateRange, RadioSelector, Select } from '@DesignSystem/controllers';
import { Button } from '@ds';
import { useModelSummaryViews, usePredicates } from '@mpm-druid/api';
import { useModelDetails } from '@mpm-druid/api/useModelDetails';
import { PredicatesPopover } from '@mpm-druid/components/PredicatesPopover';
import {
  DEFAULT_VERSION,
  INTERVAL_TYPE_OPTIONS,
  INTERVAL_TYPE_VALUES,
  MODEL_DASHBOARD_DEFAULT_VIEW_SUMMARY,
  PERFORMANCE_DATE_RANGES
} from '@mpm-druid/constants';
import { PredicateQuery } from '@mpm-druid/types';
import { DashboardViewSelect } from './DashboardViewSelect';
import { useModelViewStore } from './modelViewStore';

import styles from './DashboardHeader.module.scss';

type IntervalType = keyof typeof INTERVAL_TYPE_VALUES;

export function DashboardHeader() {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const startDate = useModelViewStore(state =>
    state.view.data.dateRange.dateStart
      ? new Date(state.view.data.dateRange.dateStart)
      : null
  );
  const endDate = useModelViewStore(state =>
    state.view.data.dateRange.dateEnd
      ? new Date(state.view.data.dateRange.dateEnd)
      : null
  );
  const intervalType = useModelViewStore(
    state => state.view.data.dateRange.intervalType
  );
  const lastDays = useModelViewStore(
    state => state.view.data.dateRange.lastDays
  );
  const modelVersion = useModelViewStore(state => state.view.data.modelVersion);
  const filterBatchId = useModelViewStore(
    state => state.view.data.filterBatchId
  );
  const updateCurrentView = useModelViewStore(state => state.updateView);
  const flushAutoSave = useModelViewStore(state => state.flushAutoSave);

  const { modelId, viewId, workspace: workspaceName } = useParams<{
    modelId: string;
    viewId?: string;
    workspace: string;
  }>();
  const history = useHistory();
  const { data: modelDetails } = useModelDetails({ modelId });
  const {
    data: modelViews,
    isLoading: isLoadingModelViews
  } = useModelSummaryViews({ modelId });
  const model = modelDetails?.model;

  const views = useMemo(() => {
    if (isLoadingModelViews) return null;

    if (modelViews?.length === 0) {
      return [MODEL_DASHBOARD_DEFAULT_VIEW_SUMMARY];
    }

    return modelViews;
  }, [isLoadingModelViews, modelViews]);

  const { data: filters } = usePredicates({ predicatesBatchId: filterBatchId });
  const validFilters = useMemo(() => {
    return filters?.filter(filter => Boolean(filter)) ?? [];
  }, [filters]);

  const versionOptions = useMemo(() => {
    const modelVersions =
      model?.versions?.map(version => ({
        label: version,
        value: version
      })) ?? [];

    return [...DEFAULT_VERSION, ...modelVersions];
  }, [model?.versions]);

  const handleChangeIntervalType = useCallback(
    (intervalType: IntervalType) => {
      updateCurrentView({ data: { dateRange: { intervalType } } });
    },
    [updateCurrentView]
  );

  const handleChangeModelVersion = useCallback(
    (version: string | null) => {
      updateCurrentView({ data: { modelVersion: version } });
    },
    [updateCurrentView]
  );

  const handleChangePredicates = useCallback(
    (_: PredicateQuery[], batchId: string) => {
      updateCurrentView({ data: { filterBatchId: batchId } });
    },
    [updateCurrentView]
  );

  const handleChangeRange = (dates: [Date, Date]) => {
    updateCurrentView({
      data: {
        dateRange: {
          dateStart: dates[0].toISOString(),
          dateEnd: dates[1].toISOString(),
          lastDays: null
        }
      }
    });
  };

  const handleSelectView = useCallback(
    (viewId: string) => {
      flushAutoSave();

      history.push(
        `/${workspaceName}/${SUB_PATHS.MODEL_PRODUCTION_MONITORING}/${modelId}/dashboard/${viewId}`
      );
    },
    [flushAutoSave, history, modelId, workspaceName]
  );

  const openFiltersPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  return (
    <div className={styles.dashboardHeader} data-test="dashboard-header">
      <div className={styles.dashboardHeaderLeftSection}>
        {views ? (
          <div className={styles.dashboardHeaderViewSelect}>
            <DashboardViewSelect
              views={views}
              selectedViewId={viewId ?? null}
              onSelectView={handleSelectView}
            />
          </div>
        ) : null}
        <Button
          size="medium"
          type="secondary"
          onClick={openFiltersPopover}
          active={!!anchorEl}
        >
          Filters {validFilters.length ? `(${validFilters.length})` : ''}
        </Button>
        <PredicatesPopover
          anchorEl={anchorEl}
          batchId={filterBatchId}
          setAnchorEl={setAnchorEl}
          handlePredicatesChange={handleChangePredicates}
          shouldPersist={false}
        />
      </div>

      <div className={styles.dashboardHeaderRightSection}>
        <Select
          onChange={handleChangeModelVersion}
          options={versionOptions}
          value={modelVersion}
          withInput
        />
        <div className={styles.dashboardHeaderDivider} />
        <Select
          onChange={handleChangeIntervalType}
          options={INTERVAL_TYPE_OPTIONS}
          value={intervalType}
        />
        <div className={styles.dashboardHeaderDivider} />
        <div className={styles.dashboardHeaderDateRanges}>
          {PERFORMANCE_DATE_RANGES[intervalType].map(days => (
            <RadioSelector
              isSelected={days === lastDays}
              key={`date-range-${days}`}
              onClick={() => {
                updateCurrentView({
                  data: {
                    dateRange: {
                      lastDays: days,
                      dateStart: null,
                      dateEnd: null
                    }
                  }
                });
              }}
              text={`${days}d`}
            />
          ))}
        </div>
        <DateRange
          startDate={startDate}
          endDate={endDate}
          onChange={handleChangeRange}
          calcMaxDate={start => calcMaxDate(new Date(), start, intervalType)}
        />
      </div>
    </div>
  );
}

const calcMaxDate = (now: Date, start: Date, intervalType: IntervalType) => {
  const currentTime = new Date(now);
  const maxInterval = PERFORMANCE_DATE_RANGES[intervalType].at(-1);
  const localMaxDate = add(start, { days: maxInterval });
  const isAllowedTime = localMaxDate <= currentTime;

  return isAllowedTime ? localMaxDate : currentTime;
};
